import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { restartableTask, didCancel } from 'ember-concurrency';
import { modifier } from 'ember-modifier';
import {
  DEFAULT_IMG,
  PLACEHOLDER,
  fallbackUrl,
  resolveAvatarUrl,
} from '@linc-technologies/linced-common/utils/avatar-url';

export default class AvatarImage extends Component {
  @service media;

  @tracked _loading = true;
  @tracked _resolvedUrl = null;

  // Priority: @url (immediate) > cached/fetched blob > gravatar > default.
  // Returns PLACEHOLDER while loading to avoid a broken-image icon.
  get url() {
    if (this.args.url) return this.args.url;
    if (this._loading) return PLACEHOLDER;
    return this._resolvedUrl ?? DEFAULT_IMG;
  }

  // Resets loading state on resource/url/mediaId change so the shimmer replays,
  // then kicks off a new fetch. The task reads mediaId after an await, where
  // autotracking can't see it, so the template passes @resource.mediaId and
  // destructuring it here re-runs the modifier when the same record gets a new
  // image.
  // eslint-disable-next-line no-unused-vars
  fetchOnResource = modifier((_element, [resource, directUrl, _mediaId]) => {
    if (directUrl) {
      this._loading = false;
      return;
    }

    this._loading = true;
    this._resolvedUrl = null;
    this.fetchBlobTask.perform(resource);
  });

  fetchBlobTask = restartableTask(async (resource) => {
    try {
      // Unwrap PromiseBelongsTo / PromiseObject proxy up front so the catch
      // below can build a gravatar URL off the record's id.
      resource = await resource;

      const url = await resolveAvatarUrl(this.media, resource);
      if (!url) {
        // No record yet -- stay loading. The modifier re-fires when @resource
        // changes and the route's model has loaded.
        return;
      }

      this._resolvedUrl = url;
      this._loading = false;
    } catch (e) {
      if (didCancel(e)) return;
      console.error('failed to fetch avatar: ' + e);
      this._resolvedUrl = fallbackUrl(resource?.id);
      this._loading = false;
    }
  });
}
